import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import type { RentManagerInteractionFormData } from '../../../../@types/contactInteraction';
import type { Owner } from '../../../../@types/Owner';
import { useAdsModal } from '../../../../hooks/useAdsModal';
import type { RootState } from '../../../../store/store';
import AdsModal from '../../../common/AdsModal/AdsModal';
import MUITable from '../../../common/MUITable';
import CoproLocalOwned from '../../components/CoproLocalOwned';
import DpeDetailsPopup from '../../components/DpeDetailsPopup';
import OwnerInfos from '../../components/OwnerInfos';
import { LastContactInfo } from '../../components/PreviousContactList';
import { formatDpeDetailsFromOwner } from '../../utils/formatters';
import InteractionForm from './InteractionForm';

interface ToContactTableProps {
  data: Owner[];
  onInteractionSubmit: (ownerId: number, formData: RentManagerInteractionFormData) => void;
  // API Pagination props
  totalCount?: number;
  onPageChange?: (page: number, rowsPerPage: number) => void;
  currentPage?: number;
  currentRowsPerPage?: number;
  onRefresh?: () => void;
}

// Create a safe wrapper for LastContactInfo that handles null/undefined owner
const SafeLastContactInfo: React.FC<{ owner: Owner }> = ({ owner }) => {
  return <LastContactInfo owner={owner} />;
};

const ToContactTable: React.FC<ToContactTableProps> = ({ 
  data, 
  onInteractionSubmit,
  totalCount,
  onPageChange,
  currentPage,
  currentRowsPerPage,
  onRefresh,
}) => {
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDpeDetailsOpen, setIsDpeDetailsOpen] = useState(false);
  const [selectedDpe, setSelectedDpe] = useState<Record<string, unknown> | null>(null);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const { handleAdsClick, adsModalProps } = useAdsModal();

  const handleContactClick = (owner: Owner) => {
    setSelectedOwner(owner);
    setIsFormOpen(true);
  };

  const handleDpeDetailsClick = (dpeDetails: Record<string, unknown>) => {
    setSelectedDpe(dpeDetails);
    setIsDpeDetailsOpen(true);
  };

  const handleFormSubmit = (formData: RentManagerInteractionFormData) => {
    if (selectedOwner) {
      onInteractionSubmit(selectedOwner.id!, formData);
      if (onRefresh) {
        onRefresh();
      }
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    if (onRefresh) {
      onRefresh();
    }
  };

  const getDpeDetails = (owner: Owner): React.ReactNode => {
    return formatDpeDetailsFromOwner(owner, handleDpeDetailsClick, handleAdsClick);
  };

  const tableData = data.map(owner => ({
    Propriétaire: <OwnerInfos owner={owner} showContacts={true} />,
    Propriété: <CoproLocalOwned owner={owner} />,
    'Dernier contact': <SafeLastContactInfo owner={owner} />,
    DPE: getDpeDetails(owner),
    Actions: (
      <button 
        className="rounded bg-rgpd-blue px-4 py-2 text-white"
        onClick={() => handleContactClick(owner)}
      >
        Prendre rendez-vous
      </button>
    ),
  }));

  const headers = [
    { title: 'Propriétaire', class: 'w-1/5' },
    { title: 'Propriété', class: 'w-1/5' },
    { title: 'Dernier contact', class: 'w-1/5' },
    { title: 'DPE', class: 'w-1/5' },
    { title: 'Actions', class: 'w-1/5' },
  ];

  return (
    <>
      <MUITable 
        data={tableData}
        headers={headers}
        isSearchable={true}
        tableClass="w-full"
        disablePagination={false}
        totalCount={totalCount}
        onPageChange={onPageChange}
        currentPage={currentPage}
        currentRowsPerPage={currentRowsPerPage}
        apiPagination={!!onPageChange}
      />

      {selectedOwner && jwt && (
        <InteractionForm
          isOpen={isFormOpen}
          onClose={handleFormClose}
          owner={selectedOwner}
          onSubmit={handleFormSubmit}
          jwt={jwt}
          onRefresh={onRefresh}
        />
      )}

      {selectedDpe && (
        <DpeDetailsPopup
          isOpen={isDpeDetailsOpen}
          onClose={() => setIsDpeDetailsOpen(false)}
          dpeDetails={selectedDpe}
        />
      )}

      <AdsModal {...adsModalProps} />
    </>
  );
};

export default ToContactTable; 